import { BarChart } from '@toggle/design-system';
import React from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { formatNumberWithUnit } from '~/widgets/earnings/utils/utils';

import styles from './MiniBarChart.module.scss';

export type MiniChartData = {
  label: string;
  value: number;
  year: number;
};

export type MiniBarChartProps = {
  data: MiniChartData[];
  yAxisLabel?: string;
};

const formatLabel = (bar: MiniChartData) => {
  const isFirstQuarter = bar.label === 'Q1';
  const isAnnual = bar.label.includes('FY');

  if (isFirstQuarter) {
    return `${bar.label}'${bar.year.toFixed().slice(-2)}`;
  }

  if (isAnnual) {
    return `${bar.label} ${bar.year.toFixed().slice(-2)}`;
  }

  return bar.label;
};

export const formatTableData = (data: MiniChartData[]) => {
  return data
    .filter(v => !['LTM', 'NTM'].includes(v.label))
    .map(bar => {
      const currentYear = new Date().getFullYear();
      const isExpectedData = bar.year > currentYear || bar.label.includes('E');

      return {
        value: bar.value,
        ...(isExpectedData && {
          borderColor:
            bar.value < 0 ? 'var(--viz-bearish)' : 'var(--viz-bullish)',
        }),
        color:
          bar.value < 0
            ? `var(--viz-bearish${isExpectedData ? '-soft' : ''})`
            : `var(--viz-bullish${isExpectedData ? '-soft' : ''})`,
        label: formatLabel(bar),
      };
    });
};

export const MiniBarChart = ({ data, yAxisLabel }: MiniBarChartProps) => {
  const bars = formatTableData(data);

  return (
    <TooltipContainer
      className={styles.tooltip}
      label={
        <BarChart
          data={bars}
          height={308}
          width={548}
          showAxisLines
          yAxisLabelFormatter={formatNumberWithUnit}
          yAxisLabel={yAxisLabel}
          barRadius={0}
        />
      }
      placement="bottom-start"
      inPortal
      hasArrow={false}
    >
      <div>
        <BarChart
          barRadius={0}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          padding={{ yAxisLabel: 0, xAxisLabel: 0 }}
          data={bars}
          height={20}
          width={40}
          showLegend={false}
          showXAxisLabels={false}
          showYAxisLabels={false}
          showAxisLines={false}
        />
      </div>
    </TooltipContainer>
  );
};
